import { Component, OnInit, Input, Inject, ViewChild, forwardRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { BaseModuleComponent } from '~Interfaces/basemodulecomponent';
import { FournisseurService } from '~Services/fournisseur.service';
import { TypeDocumentService } from '~Services/type-document.service';

import { PopupComponent } from '~Components/popup/popup.component';
import { ImageCropperComponent } from '~Components/image-cropper/image-cropper.component';

import { EtatDocumentEnum, TypeDocumentEnum } from '~Tools/Constantes';
import { Converters } from '~Tools/Converters';

/**
 * Composant de la photo fiche relais
 * @extends BaseModuleComponent
 * @implements OnInit
 */
@Component({
  selector: 'app-fiche-relais-photo',
  templateUrl: './fiche-relais-photo.component.html',
  styleUrls: ['./fiche-relais-photo.component.scss'],
    providers: [{ provide: BaseModuleComponent, useExisting: forwardRef(() => FicheRelaisPhotoComponent) }]
})
export class FicheRelaisPhotoComponent extends BaseModuleComponent implements OnInit {

    private urlPhoto: string = 'https://www.mondialrelay.fr/ww2/img/dynamique/pr.aspx?id=';

    private fichierPhoto: File = null;
    private urlNouvellePhoto: string;

    @Input() codeFournisseur: string = '';
    @Input() codePays: string = '';
    @Input() codeLieu: string = '';

    public preview: string = '';
    public photoSoumission = '';
    public messagePhotoSoumission = '';
    public messagePhoto: string = '';
    public messageRestriction: string = '';
    public afficherNotif: boolean = false;
    public activerAnimationNotif: boolean = false;
    public photoEnCoursDeValidation: boolean = false;

    public tailleMax: number;
    public filtreType: string;

    public messageErreur: string = null;

    public chargerCropper: boolean = false;

    public valeurZoom: number = 1;
    public valeurZoomOld: number = this.valeurZoom;
    public valeurRotation: number = 0;
    public valeurFichier: any = null;

    public chargementImage: boolean = false;

    @ViewChild('popupPhoto', { static: false }) popupPhoto: PopupComponent;
    @ViewChild('imageCropper', { static: false }) imageCropper: ImageCropperComponent;

    /**
     * Constructeur
     */
    constructor(
        private sanitizer: DomSanitizer,
        @Inject(FournisseurService) private fournisseurService: FournisseurService,
        @Inject(TypeDocumentService) private typeDocumentService: TypeDocumentService)
    {
        super();
    }

    /**
     * Initialisation du composant
     */
    ngOnInit() {
    }

    /**
     * Initialisation du composant module
     */
    Init() {
        this.chargementImage = false;
        this.chargerTypeDocumentPhotoPR();
        this.chargerInfosPhotoPR();
    }

    /**
     * L'URL de la photo
     */
    get url(): string {
        if (this.codePays && this.codeLieu) {
            return this.urlPhoto + this.codePays + this.codeLieu.padStart(6, '0');
        }
        return this.urlPhoto;
    }

    /**
     * L'URL de la photo de la popup
     */
    get urlPopup() {
        if (this.urlNouvellePhoto) {
            return this.sanitizer.bypassSecurityTrustResourceUrl(this.urlNouvellePhoto);
        }
    }

    /**
     * L'URL de la photo soumise
     */
    get urlPhotoSoumission() {
        if (this.photoSoumission) {
            return this.sanitizer.bypassSecurityTrustResourceUrl('data:image/png;base64, ' + this.photoSoumission);
        }
        return '';
    }

    /**
     * Sélectionner une photo
     * @param fichiers
     */
    selectionPhoto(fichiers: FileList) {
        this.preview = '';
        this.fichierPhoto = fichiers.item(0);
        var url = URL.createObjectURL(this.fichierPhoto);

        // Récupère l'image
        this.urlNouvellePhoto = url;

        // Recharger les cropper
        this.chargerCropper = false;
        this.valeurZoomOld = this.valeurZoom = 1;
        this.valeurRotation = 0;
        setTimeout(() => {
            this.chargerCropper = true;
        }, 200);
    }

    /**
     * Afficher la preview
     */
    afficherPreview() {
        this.chargementImage = true;
        setTimeout(() => {
            if (this.imageCropper) {
                this.messageErreur = null;
                this.imageCropper.crop((result) => {
                    this.chargementImage = false;
                    this.preview = result;
                });
            }
        }, 200);
    }

    /**
     * Envoyer une photo
     */
    envoyerPhoto() {
        this.messageErreur = null;
        if (this.codeFournisseur && this.fichierPhoto) {
            this.fournisseurService.soumettrePhotoFournisseur(this.codeFournisseur, this.preview).subscribe(retour => {
                // Retour OK
                if (retour && retour.Result === true) {
                    // Fermer la popup
                    if (this.popupPhoto) {
                        this.popupPhoto.fermer();
                    }
                    // Recharger les infos de la photo
                    this.chargerInfosPhotoPR();
                }
                else {
                    if (retour.StatusList && retour.StatusList.length) {
                        // Message d'erreur
                        this.messageErreur = retour.StatusList[0].Libelle;
                    }
                }
            }, err => {
                console.error(err);
            });
        }
    }

    /**
     * Marquer la photo comme vu
     */
    marquerPhotoVu() {
        if (this.activerAnimationNotif) {
            this.fournisseurService.marquerPhotoVu(this.codeFournisseur).subscribe(retour => {
                if (retour && retour.Result) {
                    this.chargerInfosPhotoPR();
                }
            }, err => {
                console.error(err);
            });
        }
    }

    /**
     * Effectuer un zoom sur le cropper
     */
    zoom() {
        let zommVal = Math.abs((this.valeurZoom*10000 - this.valeurZoomOld*10000)/10000);
        if (this.valeurZoom < this.valeurZoomOld) {
            zommVal = -zommVal;
        }
        this.valeurZoomOld = this.valeurZoom;

        this.imageCropper.zoom(zommVal);
    }

    /**
     * Effectuer une rotation sur le cropper
     */
    rotation() {
        this.imageCropper.rotateTo(this.valeurRotation);
    }

    /**
     * Définir le mode déplacement
     * @param checked
     */
    activerModeDeplacement(checked) {
        if (checked) {
            this.imageCropper.enableDragModeMove();
        }
        else {
            this.imageCropper.enableDragModeCrop();
        }
    }

    /**
     * Ouverture de la popup photo
     */
    ouvrirPopupPhoto() {
        this.chargementImage = false;
        this.chargerCropper = true;
        return true;
    }

    /**
     * Fermeture de la popup photo
     */
    fermerPopupPhoto() {
        this.chargementImage = false;
        this.chargerCropper = false;
        this.valeurZoomOld = this.valeurZoom = 1;
        this.valeurRotation = 0;
        this.urlNouvellePhoto = '';
        this.fichierPhoto = null;
        this.valeurFichier = null;
        this.messageErreur = '';
        this.preview = '';

        return true;
    }

    /**
     * Charger le type de document de la photo du PR
     */
    private chargerTypeDocumentPhotoPR() {
        this.typeDocumentService.obtenirTypeDocument(TypeDocumentEnum.PhotoPR).subscribe(retour => {
            if (retour && retour.Result) {
                this.tailleMax = retour.Result.TailleMax;
                this.filtreType = retour.Result.FiltreType;

                // Conversion octects en Mo
                var tailleMax = (this.tailleMax / 1000000).toFixed(1);
                var filtreType = this.filtreType;
                this.messageRestriction = this.T('fiche-relais-photo.restriction', tailleMax, filtreType);
            }
        }, err => {
            console.error(err);
        });
    }

    /**
     * Charger les infos sur la photo du PR
     */
    private chargerInfosPhotoPR() {
        this.fournisseurService.obtenirInformationsPhoto(this.codeFournisseur).subscribe(retour => {
            this.messagePhoto = '';
            this.messagePhotoSoumission = '';
            this.photoEnCoursDeValidation = false;
            this.activerAnimationNotif = false;
            this.afficherNotif = false;
            this.photoSoumission = '';
            if (retour && retour.Result) {
                this.gestionNotif(retour.Result);
                this.photoSoumission = retour.Result.Photo;
                this.messagePhoto = this.obtenirMessagePhoto(retour.Result);
                this.messagePhotoSoumission = this.obtenirMessagePhotoSoumission(retour.Result);
            }
        }, err => {
            console.error(err);
        });
    }

    /**
     * Obtenir le message photo
     * @param infoPhoto
     */
    private obtenirMessagePhoto(infoPhoto: any): string {
        switch (infoPhoto.Etat) {
            case EtatDocumentEnum.EnCoursDeValidation:
                return this.T('fiche-relais-photo.photo.message.encours');
            case EtatDocumentEnum.Rejete:
                return this.T('fiche-relais-photo.photo.message.rejete', infoPhoto.Motif);
            case EtatDocumentEnum.Valide:
            case EtatDocumentEnum.Aucun:
            default:
                return '';
        }
    }

    /**
     * Obtenir le message de soumission de la photo
     * @param infoPhoto
     */
    private obtenirMessagePhotoSoumission(infoPhoto: any): string {
        if (infoPhoto.DateSoumission) {
            let date: Date = new Date(infoPhoto.DateSoumission);
            let dateStr: string = Converters.convertirDateEnString(date);
            return this.T('fiche-relais-photo.photo.message.soumispar', dateStr, infoPhoto.Utilisateur);
        }
        return '';
    }

    /**
     * Gestion de la notification
     * @param infoPhoto
     */
    private gestionNotif(infoPhoto: any) {
        switch (infoPhoto.Etat) {
            case EtatDocumentEnum.EnCoursDeValidation:
                this.afficherNotif = true;
                this.activerAnimationNotif = false;
                this.photoEnCoursDeValidation = true;
                break;
            case EtatDocumentEnum.Rejete:
            case EtatDocumentEnum.Valide:
                this.activerAnimationNotif = this.afficherNotif = infoPhoto.DateVu == null && infoPhoto.DateSoumission;
                break;
            case EtatDocumentEnum.Aucun:
            default:
                this.activerAnimationNotif = this.afficherNotif = false;
                break;
        }
    }
}
